// DOM-level translator: catches ALL visible text on the page,
// including hardcoded English strings not wrapped in t().
// Uses Google Translate API in batches, with localStorage caching.

const DOM_TRANS_CACHE_KEY = 'qs_dom_trans_v2_';
const CACHE_TTL = 14 * 24 * 60 * 60 * 1000; // 14 days
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'INPUT', 'TEXTAREA', 'SELECT', 'SVG', 'CANVAS', 'NOSCRIPT']);
const MIN_LENGTH = 2;

// In-memory cache: { lang: { "original text": "translated text" } }
const memCache = {};

// Track which nodes have been translated so we can restore originals
const nodeOriginals = new WeakMap();

// Load persisted cache for a language
const loadCache = (lang) => {
  if (memCache[lang]) return memCache[lang];
  try {
    const raw = localStorage.getItem(DOM_TRANS_CACHE_KEY + lang);
    if (raw) {
      const { ts, data } = JSON.parse(raw);
      if (Date.now() - ts < CACHE_TTL) {
        memCache[lang] = data;
        return data;
      }
      localStorage.removeItem(DOM_TRANS_CACHE_KEY + lang);
    }
  } catch {}
  memCache[lang] = {};
  return memCache[lang];
};

// Save cache to localStorage
const saveCache = (lang) => {
  try {
    localStorage.setItem(DOM_TRANS_CACHE_KEY + lang, JSON.stringify({
      ts: Date.now(),
      data: memCache[lang] || {}
    }));
  } catch {}
};

// Google Translate batch
const batchTranslate = async (texts, targetLang) => {
  const joined = texts.join('\n||||\n');
  const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${encodeURIComponent(targetLang)}&dt=t&q=${encodeURIComponent(joined)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  const translated = data[0].map(seg => seg[0]).join('');
  return translated.split('\n||||\n');
};

const chunked = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

// Check if a string looks like translatable text (not just numbers, symbols, whitespace)
const isTranslatable = (text) => {
  const trimmed = text.trim();
  if (trimmed.length < MIN_LENGTH) return false;
  // Skip pure numbers, symbols, URLs, email addresses
  if (/^[\d\s\W]+$/.test(trimmed)) return false;
  if (/^https?:\/\//.test(trimmed)) return false;
  if (/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+$/.test(trimmed)) return false;
  // Skip if mostly non-Latin (already translated or not English)
  const latinChars = (trimmed.match(/[a-zA-Z]/g) || []).length;
  if (latinChars / trimmed.replace(/\s/g, '').length < 0.3) return false;
  return true;
};

// Collect all translatable text nodes from a root element
const collectTextNodes = (root) => {
  const nodes = [];
  const walker = document.createTreeWalker(
    root,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode: (node) => {
        if (!node.textContent.trim()) return NodeFilter.FILTER_REJECT;
        const parent = node.parentElement;
        if (!parent) return NodeFilter.FILTER_REJECT;
        if (SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        if (parent.closest('script, style, code, pre, .no-translate')) return NodeFilter.FILTER_REJECT;
        if (!isTranslatable(node.textContent)) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      }
    }
  );
  let node;
  while ((node = walker.nextNode())) {
    nodes.push(node);
  }
  return nodes;
};

/**
 * Translate all visible text in the document to the target language.
 * Call this after React has rendered the page.
 * @param {string} lang - Target language code (e.g. 'fr', 'de', 'ja')
 * @param {Element} root - Root element to translate (defaults to document.body)
 */
export const translateDOM = async (lang, root) => {
  if (!lang || lang === 'en') {
    // Restore originals if switching back to English
    restoreOriginals(root);
    return;
  }

  const target = root || document.body;
  if (!target) return;

  const cache = loadCache(lang);
  const textNodes = collectTextNodes(target);

  // Separate cached vs uncached
  const toTranslate = [];
  const nodeMap = new Map(); // text -> [nodes]

  textNodes.forEach(node => {
    const original = nodeOriginals.get(node) || node.textContent;
    // Store original for potential restore
    if (!nodeOriginals.has(node)) {
      nodeOriginals.set(node, node.textContent);
    }

    const text = original.trim();
    if (cache[text]) {
      // Already cached - apply immediately
      node.textContent = cache[text];
    } else {
      // Need to translate
      toTranslate.push(text);
      if (!nodeMap.has(text)) nodeMap.set(text, []);
      nodeMap.get(text).push(node);
    }
  });

  if (toTranslate.length === 0) return;

  // Deduplicate
  const uniqueTexts = [...new Set(toTranslate)];

  // Translate in parallel batches of 50
  const batches = chunked(uniqueTexts, 50);
  const batchPromises = batches.map(async (batch) => {
    try {
      const results = await batchTranslate(batch, lang);
      return batch.map((text, i) => ({ original: text, translated: results[i] || text }));
    } catch {
      return batch.map(text => ({ original: text, translated: text }));
    }
  });

  const batchResults = await Promise.all(batchPromises);

  // Apply translations and update cache
  batchResults.flat().forEach(({ original, translated }) => {
    cache[original] = translated;
    const nodes = nodeMap.get(original);
    if (nodes) {
      nodes.forEach(node => {
        // Only update if node still has the original text
        if (node.textContent.trim() === original || nodeOriginals.get(node) === original) {
          node.textContent = translated;
        }
      });
    }
  });

  saveCache(lang);
};

/**
 * Restore all text nodes back to their original English text.
 */
const restoreOriginals = (root) => {
  const target = root || document.body;
  if (!target) return;

  const walker = document.createTreeWalker(target, NodeFilter.SHOW_TEXT, null);
  let node;
  while ((node = walker.nextNode())) {
    const original = nodeOriginals.get(node);
    if (original && node.textContent !== original) {
      node.textContent = original;
    }
  }
};

/**
 * Clear all DOM translation caches for a specific language or all languages.
 */
export const clearDOMTranslationCache = (lang) => {
  if (lang) {
    delete memCache[lang];
    try { localStorage.removeItem(DOM_TRANS_CACHE_KEY + lang); } catch {}
  } else {
    Object.keys(memCache).forEach(l => delete memCache[l]);
    try {
      Object.keys(localStorage)
        .filter(k => k.startsWith(DOM_TRANS_CACHE_KEY))
        .forEach(k => localStorage.removeItem(k));
    } catch {}
  }
};
